/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useHotkeys } from 'react-hotkeys-hook';
import PropTypes from 'prop-types';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const ExportStorePrint = forwardRef(({ parent, type }, ref) => {
  const mod = 'export_store';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState({});
  const [loading, setLoading] = useState(false);
  const slipRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => slipRef.current,
  });

  const print = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      id: parent.id,
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);

        setLoading(false);

        handlePrint();
      }
    });
  };

  useImperativeHandle(ref, () => {
    return {
      print,
    };
  });

  const renderOrder2 = () => {
    return (
      <MDBox mt={0} p={2}>
        {attr.code === undefined ? null : (
          <MDBox align='center' className='card'>
            <CardContent>
              <MDBox sx={{ fontSize: 25 }}>{attr.branch_import}</MDBox>
              <MDBox sx={{ fontSize: 15 }}>{Func.dateTimeDisplay(attr.created_at)}</MDBox>
            </CardContent>
            <CardMedia
              sx={{ height: 135, width: 135, margin: 'auto' }}
              image={attr.qrcode}
              title={attr.code}
            />
            <CardContent>
              <MDBox sx={{ fontSize: 25 }}>#{attr.code}</MDBox>
              <MDBox sx={{ fontSize: 25 }}>{attr.branch_name}</MDBox>
            </CardContent>
          </MDBox>
        )}
      </MDBox>
    );
  };

  const renderOrder = () => {
    if (attr.code === undefined) return null;

    return (
      <MDBox p={1}>
        <table width='100%' cellPadding={3} cellSpacing={0}>
          <tbody>
            <tr>
              <td width='50%'>
                <img src={attr.qrcode} alt={attr.code} width='100%' />
              </td>
              <td>
                <div>{attr.export_code}</div>
                <div>{Func.dateDisplay(attr.created_at)}</div>
                <div>
                  <b>{attr.branch_code}</b>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </MDBox>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderPrint = () => {
    return (
      <div style={{ display: 'none' }}>
        <div ref={slipRef}>{type === '2' ? renderOrder2() : renderOrder()}</div>
      </div>
    );
  };

  useHotkeys('p', () => {
    if (attr.status !== 'cancel') {
      print();
    }
  });

  return (
    <div>
      {parent.status === 'cancel' ? null : (
        <Tooltip title='ปุ่มลัด : P'>
          <MDButton variant='gradient' color={type === '1' ? 'info' : 'error'} onClick={print}>
            <Icon>print</Icon>&nbsp;{t(`element.print`)}{' '}
            {parent.print_no * 1 > 0 ? `(${parent.print_no})` : ''}
          </MDButton>
        </Tooltip>
      )}
      {renderLoading()}
      {renderPrint()}
    </div>
  );
});

ExportStorePrint.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    print_no: PropTypes.string.isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
};

export default ExportStorePrint;
